import React from 'react';
import { Box, Button, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { BenchmarkRunSummary } from '../../../services/benchmarkService';
import { count } from '../BenchmarkVocabulary';
import { runSubjectsNoun, selectionLine } from '../investigation/investigationVocabulary';
import {
    RETENTION_NOTE, alertsToCases, comparisonModeLabel, kycSummary, sampleName
} from './suppliedSampleCopy';
import { dsVar, ink } from '../../../theme/tokens';

interface SuppliedRunListProps {
    runs: BenchmarkRunSummary[];
    loading?: boolean;
    error?: string | null;
    onRetry?: () => void;
    onSelect: (run: BenchmarkRunSummary) => void;
    /** The run already on screen, marked rather than offered again. */
    selectedRunKey?: number | null;
    /** Whether only the caller's own submissions are listed. */
    mineOnly?: boolean;
    /** Offers the "My runs" switch when given. */
    onMineOnlyChange?: (mineOnly: boolean) => void;
}

const formatStarted = (iso: string | null | undefined) =>
    iso
        ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
        : '—';

/**
 * Supplied-sample runs to pick from.
 *
 * A list, not a single "current" run: each run scored a different sample, so
 * the latest does not replace the earlier ones. Rows rather than a table
 * because both places this appears are narrow side panels.
 */
const SuppliedRunList: React.FC<SuppliedRunListProps> = ({
    runs, loading = false, error, onRetry, onSelect, selectedRunKey = null, mineOnly = false, onMineOnlyChange
}) => {
    const mineSwitch = onMineOnlyChange ? (
        <FormControlLabel
            control={
                <Switch
                    size="small"
                    checked={mineOnly}
                    onChange={(e) => onMineOnlyChange(e.target.checked)}
                    sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: dsVar('accent-fg') },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: dsVar('accent') }
                    }}
                />
            }
            label={<Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>My runs</Typography>}
            sx={{ mb: 1, ml: 0 }}
        />
    ) : null;

    if (error) {
        return (
            <Box sx={{ p: 2, borderRadius: '8px', border: '1px solid rgba(248, 113, 113, 0.35)', backgroundColor: 'rgba(248, 113, 113, 0.08)' }}>
                <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5') }}>{error}</Typography>
                {onRetry && (
                    <Button
                        onClick={onRetry}
                        size="small"
                        startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                        sx={{ mt: 1, textTransform: 'none', color: dsVar('accent-fg') }}
                    >
                        Try again
                    </Button>
                )}
            </Box>
        );
    }

    return (
        <Box>
            {mineSwitch}
            {loading && runs.length === 0 ? (
                <Box className="flex items-center gap-2" sx={{ py: 3, justifyContent: 'center' }}>
                    <CircularProgress size={18} sx={{ color: dsVar('accent-fg') }} />
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>Loading supplied samples…</Typography>
                </Box>
            ) : runs.length === 0 ? (
                <Box sx={{ p: 2.5, borderRadius: '8px', border: `1px dashed ${dsVar('line')}`, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
                        {mineOnly
                            ? 'You have not benchmarked a sample for this workflow yet. Runs from before submitters were recorded are listed only with "My runs" off.'
                            : 'No supplied samples have been benchmarked for this workflow yet.'}
                    </Typography>
                </Box>
            ) : (
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {runs.map((run) => {
                        const selected = run.runKey === selectedRunKey;
                        const kyc = kycSummary(run.kyc);
                        return (
                            <Box component="li" key={run.runKey}>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => onSelect(run)}
                                    aria-current={selected ? 'true' : undefined}
                                    sx={{
                                        width: '100%',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        p: 1.5,
                                        borderRadius: '8px',
                                        border: `1px solid ${selected ? 'rgba(255, 222, 89, 0.5)' : dsVar('line')}`,
                                        backgroundColor: selected ? 'rgba(255, 222, 89, 0.06)' : dsVar('page', 0.5),
                                        color: 'inherit',
                                        font: 'inherit',
                                        '&:hover': { borderColor: dsVar('accent-fg') },
                                        '&:focus-visible': { outline: `2px solid ${dsVar('accent-fg')}`, outlineOffset: 2 }
                                    }}
                                >
                                    <Box className="flex items-baseline justify-between gap-2">
                                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: dsVar('fg-body'), wordBreak: 'break-word' }}>
                                            {sampleName(run.sourceName)}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), whiteSpace: 'nowrap' }}>
                                            Run #{run.runKey}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 0.25 }}>
                                        {formatStarted(run.startedAt)}
                                        {run.submittedBy ? ` · ${run.submittedBy}` : ''}
                                    </Typography>
                                    <Box className="flex flex-wrap gap-x-3 gap-y-0.5" sx={{ mt: 0.75 }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('accent-fg'), fontWeight: 600 }}>
                                            {alertsToCases(run.rowsSupplied, run.predictionCount)}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                                            {comparisonModeLabel(run.comparisonMode)}
                                        </Typography>
                                        {run.withheldCount > 0 && (
                                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                                                {count(run.withheldCount)} withheld or qualified
                                            </Typography>
                                        )}
                                        {run.benchmarkType === 'case_investigation' && (
                                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                                                {runSubjectsNoun(run) === 'EDD reviews' ? 'EDD reviews' : 'Investigations'}
                                                {selectionLine(run.agentSelection) ? ` · ${selectionLine(run.agentSelection)}` : ''}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 0.25 }}>
                                        KYC: {kyc ?? 'none supplied'}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 1.5, lineHeight: 1.5 }}>
                {RETENTION_NOTE}
            </Typography>
        </Box>
    );
};

export default SuppliedRunList;
